import React, { useRef, useEffect, useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    SafeAreaView,
    Dimensions,
    Animated,
    Linking,
    Platform,
    Easing,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { StatusBar } from "expo-status-bar";
import Header from "../components/Header";
import FeedbackModal from "../components/FeedbackModal";
import { LinearGradient } from "expo-linear-gradient";
import { useTheme } from "../contexts/ThemeContext";
import { updateAboutPageSEO } from "../utils/seoUtils";

import * as Animatable from "react-native-animatable";
import Footer from "../components/Footer";

const { width } = Dimensions.get("window");
const isMobile = width < 768;

const colors = {
    light: {
        background: "#FAFAFA",
        section: "#FFFFFF",
        card: "#FFFFFF",
        text: "#18181B",
        subtext: "#52525B",
        accent: "#3B82F6",
        border: "#E4E4E7",
        highlight: "#F4F4F5",
        label: "#71717A",
        quote: "#3F3F46",
        line: "#E4E4E7",
        ctaGradient: ["#3B82F6", "#6366F1"],
        ctaSecondary: "rgba(255,255,255,0.2)",
        ctaText: "#FFFFFF",
        footer: "#FAFAFA",
        social: "#FFFFFF",
        copyright: "#71717A",
        shadow: "#000",
    },
    dark: {
        background: "#18181B",
        section: "#232327",
        card: "#232327",
        text: "#F2F2F2",
        subtext: "#C2C2C2",
        accent: "#60A5FA",
        border: "#34343B",
        highlight: "#232327",
        label: "#A1A1AA",
        quote: "#E4E4E7",
        line: "#34343B",
        ctaGradient: ["#6366F1", "#3B82F6"],
        ctaSecondary: "rgba(30, 41, 59, 0.5)",
        ctaText: "#F8FAFC",
        footer: "#18181B",
        social: "#232327",
        copyright: "#A0AEC0",
        shadow: "#000",
    },
};

// Custom Counter Component
interface CountUpProps {
    end: number;
    suffix?: string;
    duration?: number;
    style?: any;
}

const CountUp: React.FC<CountUpProps> = ({ end, suffix = "", duration = 2000, style }) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(0);
    const startTimeRef = useRef<number | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    useEffect(() => {
        const animate = (timestamp: number) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const progress = timestamp - startTimeRef.current;

            if (progress < duration) {
                const nextCount = Math.min(Math.floor((progress / duration) * end), end);
                if (nextCount !== countRef.current) {
                    countRef.current = nextCount;
                    setCount(nextCount);
                }
                animationFrameRef.current = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [end, duration]);

    return <Text style={style}>{count.toLocaleString()}{suffix}</Text>;
};

interface AboutScreenProps {
    navigation: any;
}

const AboutScreen: React.FC<AboutScreenProps> = ({ navigation }) => {
    const { theme } = useTheme();
    const c = colors[theme];

    const scrollRef = useRef<ScrollView>(null);
    const [isReportFormVisible, setIsReportFormVisible] = React.useState(false);

    const scrollY = useRef(new Animated.Value(0)).current;
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(30)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 800,
                useNativeDriver: true,
            }),
            Animated.spring(translateY, {
                toValue: 0,
                friction: 8,
                tension: 40,
                useNativeDriver: true,
            }),
        ]).start();
    }, []);

    useEffect(() => {
        updateAboutPageSEO();
    }, []);

    const renderFadeIn = (delay: number) => ({
        opacity: fadeAnim,
        transform: [{ translateY }],
    });

    return (
        <SafeAreaView
            style={[styles.container, { backgroundColor: c.background }]}
        >
            <StatusBar style={theme === "dark" ? "light" : "dark"} />
            <View style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100 }}>
                <Header transparent scrollY={scrollY} />
            </View>

            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ paddingBottom: 0 }}
                showsVerticalScrollIndicator={false}
                ref={scrollRef}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: false }
                )}
                scrollEventThrottle={16}
            >
                {/* Hero Section */}
                <Animated.View style={[styles.heroSection, { backgroundColor: c.background }, renderFadeIn(0)]}>
                    <LinearGradient
                        colors={theme === 'light' ? ["rgba(59, 130, 246, 0.05)", "rgba(255, 255, 255, 0)"] : ["rgba(59, 130, 246, 0.1)", "rgba(15, 23, 42, 0)"]}
                        style={styles.heroBg}
                    />
                    <View style={styles.heroContent}>


                        <Animatable.Text
                            animation="fadeInUp"
                            delay={400}
                            accessibilityRole="header"
                            aria-level={1}
                            style={[styles.heroTitle, { color: c.text }]}
                        >
                            Democratizing Access to{"\n"}
                            <Text style={{ color: c.accent }}>STEM Opportunities</Text>
                        </Animatable.Text>

                        <Animatable.Text
                            animation="fadeInUp"
                            delay={600}
                            style={[styles.heroSubtitle, { color: c.subtext }]}
                        >
                            British Columbia’s only unified directory for every STEM opportunity.
                        </Animatable.Text>
                    </View>
                </Animated.View>

                {/* Stats Section */}
                <View style={[styles.statsSection, { backgroundColor: c.background }]}>
                    <View style={styles.statsGrid}>
                        <Animatable.View
                            animation="zoomIn"
                            delay={800}
                            style={[styles.statCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <CountUp end={5} suffix="+" style={[styles.statNumber, { color: c.accent }]} />
                            <Text style={[styles.statLabel, { color: c.subtext }]}>Strategic Partnerships</Text>
                        </Animatable.View>

                        <Animatable.View
                            animation="zoomIn"
                            delay={1000}
                            style={[styles.statCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <CountUp end={6700} suffix="+" style={[styles.statNumber, { color: c.accent }]} />
                            <Text style={[styles.statLabel, { color: c.subtext }]}>Active Users</Text>
                        </Animatable.View>

                        <Animatable.View
                            animation="zoomIn"
                            delay={1200}
                            style={[styles.statCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <Text style={[styles.statNumber, { color: c.accent }]}>Top 30</Text>
                            <Text style={[styles.statLabel, { color: c.subtext }]}>Nonprofits in Canada</Text>
                        </Animatable.View>
                    </View>
                </View>

                {/* Mission Section */}
                <View style={[styles.missionSection, { backgroundColor: c.background }]}>
                    <View style={styles.sectionHeader}>
                        <Text style={[styles.sectionLabel, { color: c.accent }]}>OUR MISSION</Text>
                        <Text style={[styles.sectionTitle, { color: c.text }]}>Bridging the Gap</Text>
                    </View>

                    <View style={styles.missionGrid}>
                        <Animatable.View
                            animation="fadeInUp"
                            delay={200}
                            style={[styles.missionCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <View style={[styles.iconBox, { backgroundColor: 'rgba(59, 130, 246, 0.1)' }]}>
                                <Ionicons name="compass-outline" size={28} color={c.accent} />
                            </View>
                            <Text style={[styles.cardTitle, { color: c.text }]}>Discover</Text>
                            <Text style={[styles.cardText, { color: c.subtext }]}>
                                We curate the best STEM opportunities in British Columbia, making them easily accessible to students who need them most.
                            </Text>
                        </Animatable.View>

                        <Animatable.View
                            animation="fadeInUp"
                            delay={400}
                            style={[styles.missionCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <View style={[styles.iconBox, { backgroundColor: 'rgba(16, 185, 129, 0.1)' }]}>
                                <Ionicons name="people-outline" size={28} color="#10B981" />
                            </View>
                            <Text style={[styles.cardTitle, { color: c.text }]}>Connect</Text>
                            <Text style={[styles.cardText, { color: c.subtext }]}>
                                Building a thriving community where students, educators, and organizations collaborate to strengthen the STEM ecosystem.
                            </Text>
                        </Animatable.View>

                        <Animatable.View
                            animation="fadeInUp"
                            delay={600}
                            style={[styles.missionCard, { backgroundColor: c.card, borderColor: c.border }]}
                        >
                            <View style={[styles.iconBox, { backgroundColor: 'rgba(245, 158, 11, 0.1)' }]}>
                                <Ionicons name="rocket-outline" size={28} color="#F59E0B" />
                            </View>
                            <Text style={[styles.cardTitle, { color: c.text }]}>Innovate</Text>
                            <Text style={[styles.cardText, { color: c.subtext }]}>
                                Empowering the next generation of innovators by removing barriers to entry and fostering inclusive growth.
                            </Text>
                        </Animatable.View>
                    </View>
                </View>

                {/* Story Section */}
                <View style={[styles.storySection, { backgroundColor: c.background }]}>
                    <Animatable.View
                        animation="fadeIn"
                        duration={1500}
                        style={styles.storyContent}
                    >
                        <Text style={[styles.storyTitle, { color: c.text }]}>From Frustration to Innovation</Text>
                        <View style={[styles.quoteBox, { backgroundColor: c.card, borderLeftColor: c.accent }]}>
                            <Text style={[styles.quoteText, { color: c.quote }]}>
                                "We were tired of watching brilliant students miss out on life-changing opportunities simply because they didn't know where to look."
                            </Text>
                            <Text style={[styles.quoteAuthor, { color: c.label }]}>— Nonit Gupta</Text>
                        </View>
                        <Text style={[styles.storyText, { color: c.subtext }]}>
                            What started as a simple observation has grown into a movement. British Columbia is full of incredible STEM programs, but they were scattered across the digital landscape. We built BC STEM Opportunities to bring them all home to one beautiful, accessible platform.
                        </Text>
                    </Animatable.View>
                </View>

                {/* CTA Section */}
                <View style={styles.ctaSection}>
                    <LinearGradient
                        colors={c.ctaGradient as [string, string]}
                        start={[0, 0]}
                        end={[1, 1]}
                        style={styles.ctaGradient}
                    >
                        <Animatable.View
                            animation="fadeInUp"
                            delay={200}
                            style={styles.ctaContent}
                        >
                            <Text style={styles.ctaHeading}>Ready to Start Your Journey?</Text>
                            <Text style={styles.ctaDescription}>
                                Join thousands of students and educators transforming the future of STEM in BC.
                            </Text>
                            <View style={styles.ctaButtons}>
                                <TouchableOpacity
                                    style={styles.primaryButton}
                                    onPress={() => navigation.navigate("SignUp")}
                                >
                                    <Text style={styles.primaryButtonText}>Get Started</Text>
                                    <Ionicons name="arrow-forward" size={20} color={c.accent} />
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[styles.secondaryButton, { backgroundColor: 'rgba(255,255,255,0.15)' }]}
                                    onPress={() => setIsReportFormVisible(true)}
                                >
                                    <Text style={styles.secondaryButtonText}>Contact Us</Text>
                                </TouchableOpacity>
                            </View>
                        </Animatable.View>
                    </LinearGradient>
                </View>

                {/* Footer */}
                <Footer />
            </ScrollView>

            <FeedbackModal
                isVisible={isReportFormVisible}
                onClose={() => setIsReportFormVisible(false)}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    heroSection: {
        paddingTop: 140,
        paddingBottom: 60,
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    heroBg: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    heroContent: {
        alignItems: "center",
        paddingHorizontal: 24,
        maxWidth: 800,
        width: "100%",
    },

    heroTitle: {
        fontSize: isMobile ? 36 : 56,
        fontWeight: "800",
        textAlign: "center",
        lineHeight: isMobile ? 44 : 68,
        marginBottom: 24,
        letterSpacing: -1,
    },
    heroSubtitle: {
        fontSize: isMobile ? 18 : 20,
        lineHeight: 30,
        textAlign: "center",
        maxWidth: 600,
    },
    statsSection: {
        paddingVertical: 40,
        paddingHorizontal: 24,
    },
    statsGrid: {
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: 24,
        maxWidth: 1000,
        alignSelf: "center",
        width: "100%",
    },
    statCard: {
        flex: 1,
        padding: 24,
        borderRadius: 16,
        alignItems: "center",
        borderWidth: 1,
        minWidth: isMobile ? "100%" : 200,
    },
    statNumber: {
        fontSize: 32,
        fontWeight: "800",
        marginBottom: 8,
    },
    statLabel: {
        fontSize: 14,
        fontWeight: "500",
        textTransform: "uppercase",
        letterSpacing: 1,
        textAlign: "center",
    },
    missionSection: {
        paddingVertical: 80,
        paddingHorizontal: 24,
        alignItems: "center",
    },
    sectionHeader: {
        alignItems: "center",
        marginBottom: 48,
    },
    sectionLabel: {
        fontSize: 14,
        fontWeight: "700",
        letterSpacing: 2,
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 32,
        fontWeight: "700",
        textAlign: "center",
    },
    missionGrid: {
        flexDirection: isMobile ? "column" : "row",
        gap: 24,
        maxWidth: 1000,
        width: "100%",
    },
    missionCard: {
        flex: 1,
        padding: 32,
        borderRadius: 24,
        borderWidth: 1,
    },
    iconBox: {
        width: 56,
        height: 56,
        borderRadius: 16,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
    },
    cardTitle: {
        fontSize: 20,
        fontWeight: "700",
        marginBottom: 12,
    },
    cardText: {
        fontSize: 16,
        lineHeight: 26,
    },
    storySection: {
        paddingVertical: 80,
        paddingHorizontal: 24,
        alignItems: "center",
    },
    storyContent: {
        maxWidth: 700,
        width: "100%",
    },
    storyTitle: {
        fontSize: 28,
        fontWeight: "700",
        marginBottom: 32,
        textAlign: "center",
    },
    quoteBox: {
        padding: 32,
        borderRadius: 16,
        borderLeftWidth: 4,
        marginBottom: 32,
    },
    quoteText: {
        fontSize: 20,
        lineHeight: 32,
        fontStyle: "italic",
        marginBottom: 16,
    },
    quoteAuthor: {
        fontSize: 14,
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: 1,
    },
    storyText: {
        fontSize: 18,
        lineHeight: 30,
    },
    ctaSection: {
        padding: 24,
    },
    ctaGradient: {
        borderRadius: 32,
        padding: isMobile ? 40 : 80,
        alignItems: "center",
    },
    ctaContent: {
        maxWidth: 700,
        alignItems: "center",
    },
    ctaHeading: {
        fontSize: isMobile ? 32 : 48,
        fontWeight: "800",
        color: "#FFFFFF",
        textAlign: "center",
        marginBottom: 16,
        letterSpacing: -1,
    },
    ctaDescription: {
        fontSize: 18,
        color: "rgba(255,255,255,0.9)",
        textAlign: "center",
        marginBottom: 40,
        lineHeight: 28,
    },
    ctaButtons: {
        flexDirection: isMobile ? "column" : "row",
        gap: 16,
        width: isMobile ? "100%" : "auto",
    },
    primaryButton: {
        backgroundColor: "#FFFFFF",
        paddingHorizontal: 32,
        paddingVertical: 16,
        borderRadius: 100,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
    },
    primaryButtonText: {
        fontSize: 16,
        fontWeight: "700",
        color: "#3B82F6",
    },
    secondaryButton: {
        paddingHorizontal: 32,
        paddingVertical: 16,
        borderRadius: 100,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.3)",
    },
    secondaryButtonText: {
        fontSize: 16,
        fontWeight: "600",
        color: "#FFFFFF",
    },

});

export default AboutScreen;
